import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import { PRODUCTION_WORKSPACE } from '../contexts/workspace.logic';
import { rackEquipmentQueryKey } from './equipmentWrite.logic';

export interface RackEquipmentItem {
  id: string;
  name: string;
  equipment_type: string;
  u_height: number;
  u_position: number;
  mounting_position: string;
  is_zero_u?: boolean;
  weight_kg?: number;
  power_w?: number;
  serial_number?: string;
  catalog_id?: number | null;
  manufacturer?: string;
  model?: string;
  asset_tag?: string;
  device_role?: string;
  status?: string;
  airflow?: string;
  depth?: string;
  tenant?: string;
  attributes?: any;
}

export function useRackWorkspaceData(actualCode: string, actualRackId: string | undefined, activeWorkspaceId: string = PRODUCTION_WORKSPACE) {
  const queryClient = useQueryClient();

  // 1. Racks List
  const { data: racksList = [], isLoading: isRacksLoading } = useQuery({
    queryKey: ['racks', actualCode],
    queryFn: () => api.get(`/api/datacenters/${actualCode}/racks`).then(res => res.data),
    enabled: !!actualCode,
  });

  // 2. Rack Data
  const { data: rackData } = useQuery({
    queryKey: ['rackConfig', actualCode, actualRackId],
    queryFn: () => api.get(`/api/datacenters/racks/${actualRackId}`).then(res => res.data),
    enabled: !!actualCode && !!actualRackId,
    staleTime: 5 * 60 * 1000,
  });

  // 3. Equipment List
  const { data: equipmentList = [], isLoading: isEqLoading } = useQuery<RackEquipmentItem[]>({
    queryKey: [...rackEquipmentQueryKey(actualCode, actualRackId, activeWorkspaceId)],
    queryFn: () => api.get(`/api/datacenters/${actualCode}/racks/${actualRackId}/equipment?workspace=${activeWorkspaceId}`).then(res => res.data),
    enabled: !!actualRackId,
  });

  // 4. DC Info
  const { data: dcInfo } = useQuery({
    queryKey: ['dc-info', actualCode],
    queryFn: () => api.get(`/api/datacenters/${actualCode}`).then(res => res.data),
    enabled: !!actualCode,
  });

  // Derived state
  const isLoading = isEqLoading || isRacksLoading;
  const currentRack = useMemo(() => racksList.find((r: any) => String(r.id) === String(actualRackId)) || null, [racksList, actualRackId]);
  const slots = currentRack?.u_slots || 42;

  // Adjacent racks for navigation
  const { prevRack, nextRack } = useMemo(() => {
    if (!currentRack || !racksList.length) return { prevRack: null, nextRack: null };
    const sameRow = [...racksList]
      .filter((r: any) => r.row_index === currentRack.row_index)
      .sort((a: any, b: any) => (a.rack_index ?? 0) - (b.rack_index ?? 0));
    const idx = sameRow.findIndex((r: any) => String(r.id) === String(actualRackId));
    return {
      prevRack: idx > 0 ? sameRow[idx - 1] : null,
      nextRack: idx >= 0 && idx < sameRow.length - 1 ? sameRow[idx + 1] : null,
    };
  }, [racksList, currentRack, actualRackId]);

  return {
    racksList,
    rackData,
    equipmentList,
    dcInfo,
    isLoading,
    currentRack,
    slots,
    prevRack,
    nextRack,
    queryClient
  };
}
